/**
 * Acquisition calculation („Ankaufskalkulation", Objekt anlegen Schritt 4, `property_calculation`), engine of
 * Nico Kremer's model (owner decision Paul 30.10.2026: „Das soll genau so sein wie er es ursprünglich gebaut
 * hat.", ABWEICHUNGEN D-351).
 *
 * A port of `calcAnkauf()` (`packages/shared/test-fixtures/nico-ankauf-engine.cjs`, the verified port of Nico's
 * Excel „Ankaufskalkulation_Adolphstrasse_25_Koeln.xlsx", Stand 26.06.2026): a multi-family house is bought
 * (Kaufpreis + Käufer-Maklercourtage + Grunderwerbsteuer + Notar/Grundbuch = Einstand; + Cap-Ex, Finanzierungs-
 * und Teilungskosten = Gesamtinvestitionskosten, GIK), divided into units and sold one by one at the target price
 * per m² (VK-Basis); the sales commission comes off the VK-Basis (Abgabepreis), the margin is Abgabe − GIK. Per
 * unit the end buyer's monthly burden is modelled (interest + principal on the price plus the buyer's acquisition
 * costs, running costs, actual rent, tax effect of interest + costs + AfA − rent); a residential unit with a
 * positive net payment gets the rent subsidy (actual rent × subsidy % for N months, paid once, buyer-financed on
 * top of the VK-Basis).
 *
 * The arithmetic runs in euros exactly in Nico's order; inputs and money results are whole cents (rounded half
 * away from zero, like `investment.ts`), rates are plain percentages, factors plain numbers. Nico's sign
 * convention is kept: a positive net figure is a monthly payment of the buyer, a negative one a surplus.
 *
 * Nico's feedback of 01.11.2026 (owner decision 02.11.2026, D-369…D-372) adds three options; without them the
 * engine is his sheet unchanged (the parity test runs without them):
 * - `acquisitionCostsPayer` „Kaufnebenkosten zahlt": buyer (default) = Einstand = KP × (1 + NK %) as in his sheet;
 *   seller = the NK are carved out of the agreed price, Einstand = KP ÷ (1 − NK %) (his „Kaufpreis ÷ 91,5 × 100"),
 *   Courtage / Grunderwerbsteuer / Notar are their percentages of that price incl. NK. NK % = Courtage + GrESt +
 *   Notar.
 * - `commercialSplit` „Gewerbeanteil nach" Fläche / Miete: with at least one Gewerbe unit the commercial share of
 *   the Einstand (his „GIK-Ankauf") comes off — share = commercial area ÷ total area, or commercial rent ÷ total
 *   rent (by area when the unit list has no rent) — then Cap-Ex, financing and division costs are added in full
 *   („GIK Wohnen"). VK-Basis, sales commission, Abgabe and margin are computed on the residential units only; the
 *   VK-Faktor is VK-Basis ÷ residential rent. Absent / null = the whole building is sold (his sheet).
 * - `mode` „Rechenweg": forward (default) = Kaufpreis → Marge; target_margin = the highest purchase price (whole
 *   cents) whose margin on the (residential) GIK is not below `targetMarginPct`. Abgabe does not depend on the
 *   purchase price, so the price follows in closed form: GIK = Abgabe ÷ (1 + m), KP = (GIK − Cap-Ex − Finanzierung
 *   − Teilung) ÷ (f × (1 − Gewerbeanteil)) with f = 1 + NK % (buyer) or 1 ÷ (1 − NK %) (seller), floored to the
 *   cent and then corrected cent by cent against the forward calculation (floating point); never below 0. Every
 *   other figure is then the forward calculation at that price.
 */
/**
 * Nico's defaults for a new calculation (his calculator's parameters; the purchase-side commission is 0 as in his
 * sheet, the land transfer tax comes from the property's state, D-199). Kaufpreis and Ziel-Verkaufspreis have no
 * default.
 */
export const ACQUISITION_DEFAULTS = {
    brokerCommissionPct: 0,
    notaryPct: 2,
    capexCents: 0,
    financingCostsCents: 0,
    divisionCostsCents: 0,
    salesCommissionPct: 15,
    interestPct: 4.7,
    repaymentPct: 1.3,
    depreciationPct: 4,
    buildingSharePct: 80,
    taxRatePct: 42,
    buyerAcquisitionCostsPct: 0,
    unitManagementCents: 3_500,
    hoaManagementCents: 3_500,
    maintenancePerSqmCents: 50,
    otherCostsPerSqmCents: 25,
    rentSubsidyPct: 15,
    rentSubsidyMonths: 36,
};
/** Highest number of units of one calculation. */
export const ACQUISITION_MAX_UNITS = 500;
/** Below this the margin on GIK counts as reaching the target (floating point of the closed form, in pp). */
const TARGET_MARGIN_TOLERANCE_PP = 1e-10;
/** NK % = Courtage + Grunderwerbsteuer + Notar (plain percent). */
export function acquisitionCostsPctOf(input) {
    return input.brokerCommissionPct + input.landTransferTaxPct + input.notaryPct;
}
/**
 * Nico's figures. Forward: at the given purchase price. Target margin: at the highest whole-cent purchase price whose
 * margin on the (residential) GIK reaches `targetMarginPct` (closed form, then cent-exact against this forward
 * calculation; see the module comment). Throws a RangeError when the seller pays NK of 100 % or more (the price
 * incl. NK would be infinite) — the schemas reject that input first.
 */
export function calculateAcquisition(input) {
    if (input.acquisitionCostsPayer === 'seller' && acquisitionCostsPctOf(input) >= 100)
        throw new RangeError('acquisition costs of 100 % or more cannot be carved out of the price');
    const price = input.mode === 'target_margin' ? maxPurchasePriceCents(input) : input.purchasePriceCents;
    const { result } = calculateForward(input, price, input.mode === 'target_margin' ? price : null);
    return result;
}
function maxPurchasePriceCents(input) {
    const target = input.targetMarginPct ?? 0;
    const reaches = (priceCents) => {
        const { raw } = calculateForward(input, priceCents, priceCents);
        return raw.gikResidential > 0 && raw.marginPct >= target - TARGET_MARGIN_TOLERANCE_PP;
    };
    const { raw: probe } = calculateForward(input, 0, 0);
    const nk = acquisitionCostsPctOf(input) / 100;
    const factor = input.acquisitionCostsPayer === 'seller' ? 1 / (1 - nk) : 1 + nk;
    const residentialFactor = factor * (1 - probe.commercialShare);
    if (residentialFactor <= 0)
        return 0;
    const extras = (input.capexCents + input.financingCostsCents + input.divisionCostsCents) / 100;
    const gikTarget = probe.handover / (1 + target / 100);
    let cents = Math.max(0, Math.floor(((gikTarget - extras) / residentialFactor) * 100));
    // the margin falls strictly with the price: step to the last cent that still reaches the target
    while (reaches(cents + 1))
        cents += 1;
    while (cents > 0 && !reaches(cents))
        cents -= 1;
    return cents;
}
function calculateForward(input, purchasePriceCents, maxPurchasePrice) {
    const P = {
        kaufpreis: purchasePriceCents / 100,
        courtage: input.brokerCommissionPct / 100,
        grest: input.landTransferTaxPct / 100,
        notar: input.notaryPct / 100,
        capex: input.capexCents / 100,
        finkosten: input.financingCostsCents / 100,
        teilung: input.divisionCostsCents / 100,
        zielVK: input.targetSalePricePerSqmCents / 100,
        provision: input.salesCommissionPct / 100,
        zins: input.interestPct / 100,
        tilgung: input.repaymentPct / 100,
        afa: input.depreciationPct / 100,
        gebAnteil: input.buildingSharePct / 100,
        steuersatz: input.taxRatePct / 100,
        enk: input.buyerAcquisitionCostsPct / 100,
        sev: input.unitManagementCents / 100,
        weg: input.hoaManagementCents / 100,
        instand: input.maintenancePerSqmCents / 100,
        sonstNK: input.otherCostsPerSqmCents / 100,
        subvPct: input.rentSubsidyPct / 100,
        subvMonate: input.rentSubsidyMonths,
    };
    let flaeche = 0;
    let mieteM = 0;
    let gewerbeFlaeche = 0;
    let gewerbeMieteM = 0;
    for (const e of input.units) {
        flaeche += e.areaSqm;
        mieteM += e.rentCents / 100;
        if (!e.residential) {
            gewerbeFlaeche += e.areaSqm;
            gewerbeMieteM += e.rentCents / 100;
        }
    }
    const mieteJ = mieteM * 12;
    // Nico 01.11.2026: Käufer zahlt NK → KP ÷ 100 × 108,5; Verkäufer zahlt NK → KP ÷ 91,5 × 100 (D-371)
    const nk = P.courtage + P.grest + P.notar;
    const preisBasis = input.acquisitionCostsPayer === 'seller' ? P.kaufpreis / (1 - nk) : P.kaufpreis;
    const courtageEur = preisBasis * P.courtage;
    const grestEur = preisBasis * P.grest;
    const notarEur = preisBasis * P.notar;
    const einstand = input.acquisitionCostsPayer === 'seller' ? preisBasis : P.kaufpreis + courtageEur + grestEur + notarEur;
    const gik = einstand + P.capex + P.finkosten + P.teilung;
    // Nico 01.11.2026: „GIK-Ankauf minus den Gewerbeanteil … dann rechne ich die CapEx drauf" (D-372)
    let gewerbeAnteil = 0;
    let splitBasis = null;
    if (input.commercialSplit && gewerbeFlaeche + gewerbeMieteM > 0 && (flaeche > 0 || mieteM > 0)) {
        splitBasis = input.commercialSplit === 'rent' && mieteM > 0 ? 'rent' : 'area';
        gewerbeAnteil =
            splitBasis === 'rent' ? gewerbeMieteM / mieteM : flaeche > 0 ? gewerbeFlaeche / flaeche : 0;
        if (gewerbeAnteil === 0)
            splitBasis = null;
    }
    const gewerbeEur = einstand * gewerbeAnteil;
    const gikWohnen = gik - gewerbeEur;
    const vkFlaeche = splitBasis === null ? flaeche : flaeche - gewerbeFlaeche;
    const vkMieteJ = splitBasis === null ? mieteJ : (mieteM - gewerbeMieteM) * 12;
    let subvSumme = 0;
    const units = input.units.map((e) => {
        const miete = e.rentCents / 100;
        const vkp = e.areaSqm * P.zielVK;
        const finBasis = vkp * (1 + P.enk);
        const zinsM = (finBasis * P.zins) / 12;
        const tilgM = (finBasis * P.tilgung) / 12;
        const rate = zinsM + tilgM;
        const inst = e.areaSqm * P.instand;
        const snk = e.areaSqm * P.sonstNK;
        const afaM = (finBasis * P.gebAnteil * P.afa) / 12;
        const abzugsf = zinsM + P.sev + P.weg + snk;
        const verlust = abzugsf + afaM - miete;
        const steuervorteil = verlust * P.steuersatz;
        const bruttoOhne = zinsM + tilgM + P.sev + P.weg + inst + snk - miete;
        const nettoOhne = bruttoOhne - steuervorteil;
        const aktiv = nettoOhne > 0 && e.residential ? 1 : 0;
        const einmal = miete * P.subvPct * P.subvMonate * aktiv;
        const cfMiete = miete * (1 + P.subvPct * aktiv);
        const bruttoInkl = zinsM + tilgM + P.sev + P.weg + inst + snk - cfMiete;
        const nettoInkl = bruttoInkl - steuervorteil;
        subvSumme += einmal;
        return {
            position: e.position,
            number: e.number,
            areaSqm: e.areaSqm,
            rentCents: e.rentCents,
            residential: e.residential,
            salePriceCents: toCents(vkp),
            installmentCents: toCents(rate),
            taxBenefitCents: toCents(steuervorteil),
            netWithoutSubsidyCents: toCents(nettoOhne),
            subsidyActive: aktiv === 1,
            subsidyOneOffCents: toCents(einmal),
            netWithSubsidyCents: toCents(nettoInkl),
        };
    });
    const vkBasis = P.zielVK * vkFlaeche;
    const provEur = vkBasis * P.provision;
    const abgabe = vkBasis - provEur;
    const marge = abgabe - gikWohnen;
    const margePct = gikWohnen ? (marge / gikWohnen) * 100 : 0;
    const result = {
        totalAreaSqm: Math.round(flaeche * 100) / 100,
        rentMonthlyCents: toCents(mieteM),
        rentAnnualCents: toCents(mieteJ),
        brokerCommissionCents: toCents(courtageEur),
        landTransferTaxCents: toCents(grestEur),
        notaryCents: toCents(notarEur),
        acquisitionCostCents: toCents(einstand),
        totalInvestmentCents: toCents(gik),
        commercialShareCents: toCents(gewerbeEur),
        commercialSharePct: gewerbeAnteil * 100,
        commercialSplitBasis: splitBasis,
        gikResidentialCents: toCents(gikWohnen),
        effectivePurchasePriceCents: purchasePriceCents,
        maxPurchasePriceCents: maxPurchasePrice,
        acquisitionCostsPct: nk * 100,
        saleBaseCents: toCents(vkBasis),
        rentSubsidyTotalCents: toCents(subvSumme),
        saleInclSubsidyCents: toCents(vkBasis + subvSumme),
        salesCommissionCents: toCents(provEur),
        handoverPriceCents: toCents(abgabe),
        marginCents: toCents(marge),
        marginOnTotalInvestmentPct: margePct,
        purchaseFactor: mieteJ ? P.kaufpreis / mieteJ : 0,
        saleFactor: vkMieteJ ? vkBasis / vkMieteJ : 0,
        grossYieldPct: P.kaufpreis ? (mieteJ / P.kaufpreis) * 100 : 0,
        salePricePerSqmCents: vkFlaeche ? toCents(vkBasis / vkFlaeche) : 0,
        units,
    };
    return {
        result,
        raw: { gikResidential: gikWohnen, handover: abgabe, commercialShare: gewerbeAnteil, marginPct: margePct },
    };
}
/** Whole cents, rounded half away from zero (never −0). */
function toCents(euros) {
    const cents = Math.round(Math.abs(euros) * 100);
    return cents === 0 ? 0 : euros < 0 ? -cents : cents;
}
//# sourceMappingURL=acquisition.js.map